import {
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useToast,
} from '@chakra-ui/react';
import { mdiDotsHorizontal } from '@mdi/js';
import { PortalIcon } from 'features/common/components/PortalIcon';
import { history } from 'features/common/history/history';
import {
  getContextIdDetailsDeleteRoute,
  getContextIdsEditRoute,
} from 'features/context-ids/routes';
import { ContextNameSection } from '../ContextNameSection';
import { useGetTenantsQuery } from 'gql/graphql';
import { env as envConfig } from '../../../../../common/config/envConfig';
import { useMemo } from 'react';
import { TenantApplicationNode } from '../../../../types/types';
import { handleRegenerateInNewTab } from '../../../../utils/regenerationUtils';
import { useGetSitecoreContextId } from '../../../../api/useGetSitecoreContextId';

const getActions = (type?: string, onRegenerate?: () => void) => [
  {
    id: 'edit-context-id-action-context-id-details-page',
    label: 'Edit',
    getPath: getContextIdsEditRoute,
    dataBehaviorAnalyticsId: 'edit-context-id-action-context-id-details-page',
  },
  ...(type === 'custom'
    ? [
        {
          id: 'delete-context-id-action-context-id-details-page',
          label: 'Delete',
          getPath: getContextIdDetailsDeleteRoute,
          dataBehaviorAnalyticsId:
            'delete-context-id-action-context-id-details-page',
        },
      ]
    : [
        {
          id: 'regenerate-context-id-action-context-id-details-page',
          label: 'Regenerate',
          onClick: onRegenerate,
          dataBehaviorAnalyticsId:
            'regenerate-context-id-action-context-id-details-page',
        },
      ]),
];

export const Header = ({ name, type }: { name: string; type?: string }) => {
  const toast = useToast();

  const { data: tenantsData } = useGetTenantsQuery({
    variables: {
      first: 100,
      env: envConfig,
    },
  });

  const { data: contextData } = useGetSitecoreContextId({
    sitecoreContextId: name,
  });

  const applicationsNodes: TenantApplicationNode[] = useMemo(() => {
    const rawNodes = tenantsData?.user?.applications?.nodes || [];
    return rawNodes.map((node) => ({
      id: node.id,
      annotations:
        node.annotations?.map((a) => ({
          key: a.key,
          value: a.value,
        })) || [],
    }));
  }, [tenantsData]);

  const handleRegenerate = async () => {
    await handleRegenerateInNewTab(
      name,
      contextData?.resources,
      applicationsNodes,
      toast,
    );
  };

  return (
    <Flex data-testid='app-details-header' justifyContent='space-between'>
      <ContextNameSection name={name} />
      <Menu placement='bottom-end'>
        <MenuButton
          onClick={(event) => event.stopPropagation()}
          as={IconButton}
          size='sm'
          variant='ghost'
          icon={<PortalIcon path={mdiDotsHorizontal} />}
        />
        <MenuList>
          {getActions(type, handleRegenerate).map(
            ({ id, label, getPath, onClick, dataBehaviorAnalyticsId }) => (
              <MenuItem
                key={id}
                onClick={() =>
                  onClick
                    ? onClick()
                    : getPath &&
                      history.push({
                        pathname: getPath(name),
                        search: history.location.search,
                      })
                }
                data-behavior-analytics-id={dataBehaviorAnalyticsId}
              >
                {label}
              </MenuItem>
            ),
          )}
        </MenuList>
      </Menu>
    </Flex>
  );
};
